import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunk to fetch all command types
export const fetchCommandTypes = createAsyncThunk(
  'commandTypes/fetchCommandTypes',
  async ({ token, profileId, language = 'en' }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/commands`,
        {
          method: 'GET',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk to fetch a specific command type
export const fetchCommandType = createAsyncThunk(
  'commandTypes/fetchCommandType',
  async ({ token, profileId, commandName, language = 'en' }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/commands/${commandName}`,
        {
          method: 'GET',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

const initialState = {
  commandTypes: [],
  commandTypesMap: {}, // Map of command name to full details
  loading: false,
  error: null,
  language: 'en',
}

const commandTypesSlice = createSlice({
  name: 'commandTypes',
  initialState,
  reducers: {
    setLanguage: (state, action) => {
      state.language = action.payload
    },
    clearCommandTypes: (state) => {
      state.commandTypes = []
      state.commandTypesMap = {}
      state.loading = false
      state.error = null
    },
    loadCommandTypesFromStorage: (state, action) => {
      // Load command types from localStorage for a specific language
      const { commandTypes, language } = action.payload
      state.commandTypes = commandTypes || []
      state.language = language || 'en'

      // Rebuild the map
      state.commandTypesMap = {}
      commandTypes?.forEach((commandType) => {
        state.commandTypesMap[commandType.name] = commandType
      })

      state.loading = false
      state.error = null
    },
    clearCommandTypesForLanguageSwitch: (state) => {
      // Clear command types when switching languages to force re-fetch
      state.commandTypes = []
      state.commandTypesMap = {}
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all command types
      .addCase(fetchCommandTypes.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCommandTypes.fulfilled, (state, action) => {
        state.loading = false
        state.commandTypes = action.payload.commandTypes || []

        // Store the language that was used for this fetch
        state.language = action.meta.arg.language || 'en'

        // Build a map for quick lookup
        state.commandTypesMap = {}
        action.payload.commandTypes?.forEach((commandType) => {
          state.commandTypesMap[commandType.name] = commandType
        })
      })
      .addCase(fetchCommandTypes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch specific command type
      .addCase(fetchCommandType.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCommandType.fulfilled, (state, action) => {
        state.loading = false
        const commandType = action.payload

        // Update the map with the specific command type
        state.commandTypesMap[commandType.name] = commandType

        // Update the array if it doesn't exist
        const existingIndex = state.commandTypes.findIndex((ct) => ct.name === commandType.name)
        if (existingIndex === -1) {
          state.commandTypes.push(commandType)
        } else {
          state.commandTypes[existingIndex] = commandType
        }
      })
      .addCase(fetchCommandType.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const {
  setLanguage,
  clearCommandTypes,
  loadCommandTypesFromStorage,
  clearCommandTypesForLanguageSwitch,
} = commandTypesSlice.actions

// Selectors
export const selectCommandTypes = (state) => state.commandTypes.commandTypes
export const selectCommandTypesMap = (state) => state.commandTypes.commandTypesMap
export const selectCommandType = (state, commandName) =>
  state.commandTypes.commandTypesMap[commandName]
export const selectCommandTypesLoading = (state) => state.commandTypes.loading
export const selectCommandTypesError = (state) => state.commandTypes.error
export const selectCommandTypesLanguage = (state) => state.commandTypes.language

export default commandTypesSlice.reducer
